import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import type { ActionItemProps } from './actionRegistry';
import { StatusIndicator } from './StatusIndicator';
import { getItemStyles } from './itemStyles';

const COPY = {
  info: 'Scheduling calls for candidates in other timezones. The calls will be queued and will be taken at earliest time.',
  infoSameTimezone:
    'Scheduling calls for candidates. The calls will be queued and will be taken at earliest time.',
  inProgressVerb: 'Queueing',
  completedVerb: 'Scheduled',
  failedVerb: 'Failed to schedule',
  timeEstimate: 'Less than a minute left',
};

/**
 * Bulk-operation queue item for AI coordinator dispatch.
 * Replaces the generic single-line `DefaultItem` with the multi-candidate-aware
 * card shown when scheduling calls. The info banner mentions "other timezones"
 * only when candidates span more than one timezone (item.meta.allSameTimezone).
 */
export const Coordinator = ({ item }: ActionItemProps) => {
  const styles = getItemStyles(item.status);
  const isFailed = item.status === 'failed';
  const isCompleted = item.status === 'completed';
  const count = item.total ?? item.successCount ?? 0;
  const noun = count === 1 ? 'call' : 'calls';
  const info = item.meta?.allSameTimezone ? COPY.infoSameTimezone : COPY.info;

  let verb: string;
  if (isFailed) verb = COPY.failedVerb;
  else if (isCompleted) verb = COPY.completedVerb;
  else verb = COPY.inProgressVerb;

  const summary = `${verb} ${count} ${noun}${isCompleted ? '' : '...'}`;

  return (
    <Box
      py="14px"
      px="20px"
      borderBottom="1px solid"
      borderColor={styles.borderColor}
      bg={styles.container.bg}
      _first={{ borderTop: '1px solid', borderColor: styles.borderColor }}
      _last={{
        borderBottom: isFailed ? '1px solid' : 'none',
        borderColor: styles.borderColor,
      }}
    >
      <Box
        bg="secondary-25"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="8px"
        p="12px 14px"
        mb="14px"
      >
        <KText fontSize="13px" color="secondary-700" lineHeight="20px">
          {info}
        </KText>
      </Box>

      <Flex alignItems="center" justifyContent="space-between" gap="12px">
        <KText size="sm" color={styles.text.color}>
          {summary}
        </KText>
        <Flex alignItems="center" gap="8px" flexShrink={0}>
          {!isFailed && !isCompleted && (
            <KText size="sm" color="secondary-700">
              {COPY.timeEstimate}
            </KText>
          )}
          <StatusIndicator
            status={item.status}
            progress={item.progress}
            errorType={item.errorType}
          />
        </Flex>
      </Flex>
    </Box>
  );
};
